import flattenDeep from 'lodash/flattenDeep';
import find from 'lodash/find';
import intersection from 'lodash/intersection';
import uniqBy from 'lodash/uniqBy';
import omit from 'lodash/omit';
import { observable, makeObservable, action } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { Device } from '@/store/Station/constants';
import {
  EngineStepsEnum,
  ruleEditableFields,
  SetpointsConfiguration as SetpointsConfigurationConstant,
  SetpointsMappping,
} from './constants';
import {
  convertPressureToImperial,
  convertNullToInfinity,
  sortSetpointsByElementIndex,
  createBandsetpoints,
  convertSetpointValues,
  validateSetpoints,
} from './utils';
import { Compressors } from './Compressors';
import { Setpoint, MetricSetpoint } from './common';
import { DeviceMetadata } from '@/store/Devices/constants';
import { MainRangesConfiguration } from '@/store/Engine/MainRangesConfiguration';

export class SetpointsConfiguration {
  @observable setpoints: Setpoint[];
  @observable oldValues: Partial<SetpointsConfiguration>;
  @observable sortedCompressors?: DeviceMetadata[];

  constructor(
    setpointsConfiguration?: SetpointsConfiguration,
    compressors?: Compressors,
    compressorsList: Device[] = [],
    compressorDevices = [],
    tvalue?,
    unitMeasurementSystem?,
    isDefault?,
    sortedCompressors?: DeviceMetadata[],
  ) {
    makeObservable(this);
    this.sortedCompressors = sortedCompressors;
    this.setpoints = Setpoint.convertSetpointsToUnitMeasurementSystem(
      setpointsConfiguration?.setpoints,
      tvalue,
      unitMeasurementSystem,
      convertPressureToImperial,
    ) ?? [
      ...flattenDeep(
        compressors?.compressorsGroups?.map(
          compressorGroup =>
            compressorGroup?.compressors?.map(
              compressor =>
                new Setpoint({
                  key: uuidv4(),
                  compressorId: compressor,
                  compressorGroupId: compressorGroup?.id,
                }),
            ) ?? [],
        ),
      ),
      ...compressorsList.reduce(
        (result, current) => [
          ...result,
          ...(!compressors.isCompressorInAnyGroup(current?.id)
            ? [
                new Setpoint({
                  key: uuidv4(),
                  compressorId: current?.deviceId,
                  compressorNumber: current?.compressorNumber,
                  compressorName: current?.name,
                  modulate: intersection(find(compressorDevices, { id: current?.deviceId })?.actions, [
                    'modulationOn',
                    'modulationOff',
                  ])?.length
                    ? false
                    : undefined,
                }),
              ]
            : []),
        ],
        [],
      ),
    ];

    const oldSetpointsConfiguration = window.localStorage.getItem(
      isDefault ? EngineStepsEnum.DefaultSetpointsConfiguration : SetpointsConfigurationConstant,
    );
    if (oldSetpointsConfiguration)
      this.oldValues = {
        setpoints: Setpoint.convertSetpointsToUnitMeasurementSystem(
          JSON.parse(oldSetpointsConfiguration, convertNullToInfinity)?.setpoints,
          tvalue,
          unitMeasurementSystem,
          convertPressureToImperial,
        ),
      };
  }

  @action setSetpointField(key, field, value) {
    this.setpoints = this.setpoints?.map(setpoint =>
      setpoint?.key === key ? new Setpoint({ ...setpoint, [field]: value }) : setpoint,
    );
  }

  @action cancelChanges() {
    this.setpoints = this.setpoints?.map(setpoint => {
      const oldDefaultSetpoint = find(this.oldValues?.setpoints, { key: setpoint.key });
      return new Setpoint({
        ...setpoint,
        load: oldDefaultSetpoint?.load,
        unload: oldDefaultSetpoint?.unload,
        autoStop: oldDefaultSetpoint?.autoStop,
        immediateStop: oldDefaultSetpoint?.immediateStop,
        vsdTarget: oldDefaultSetpoint?.vsdTarget,
        modulate: oldDefaultSetpoint?.modulate,
        bandConfiguration: oldDefaultSetpoint?.bandConfiguration,
      });
    });
  }

  hasChanges() {
    return this.setpoints?.some(setpoint =>
      ruleEditableFields.some(
        field => setpoint?.[field] !== find(this.oldValues?.setpoints, { key: setpoint.key })?.[field],
      ),
    );
  }

  getSetpointsByPressureRange(pressureRangeKey?: string): Setpoint[] {
    const filteredSetpoints = this.setpoints?.filter(setpoint =>
      pressureRangeKey ? setpoint?.parentRangeKey === pressureRangeKey : !setpoint?.parentRangeKey,
    );
    return sortSetpointsByElementIndex(filteredSetpoints, this.sortedCompressors);
  }

  getUniqueRangeSetpoints() {
    return uniqBy(this.setpoints, 'parentRangeKey');
  }

  getOldSetpointByKey(key) {
    return find(this.oldValues?.setpoints, { key });
  }

  getValuesToSave(tvalue, unitMeasurementSystem, devicesStore, engineMetrics: MetricSetpoint[] = []) {
    const setpoints = this.setpoints?.map(setpoint => {
      const compressor = find(devicesStore.devicesMetadata, { id: setpoint.compressorId });
      const editableFields = compressor?.editableFields || [];
      const mappedEditableFields = editableFields.reduce((acc, field) => {
        const mappedField = SetpointsMappping[field];
        if (mappedField) {
          acc.push(mappedField);
        }
        return acc;
      }, []);

      const bandsetpoints = createBandsetpoints(setpoint, engineMetrics);

      return convertSetpointValues(setpoint, mappedEditableFields, tvalue, unitMeasurementSystem, bandsetpoints);
    });
    return omit({ ...this, setpoints }, ['sortedCompressors', 'oldValues']);
  }

  validate(devicesMetadata: DeviceMetadata[], mainRangesConfiguration?: MainRangesConfiguration) {
    return validateSetpoints(this.setpoints, devicesMetadata, mainRangesConfiguration);
  }
}
